import { useEffect, useRef, useState } from "react";
import useDrag from "./DragHandler";
import useClickOutside from "./ClickOutsideHandler";
import { XCircleIcon } from "@heroicons/react/16/solid";
import { USER_TYPE } from "@constants/Enums";
import { METHOD_TYPE_TRANSLATIONS } from "@services/TranslateCsvHeaders";
import { FailedUsersTable } from "./components/FailedUsersTable";
import { FailedDepartmentsTable } from "./components/FailedDepartmentsTable";

interface FailedUploadsModalProps {
  failedUploads: [] | undefined;
  confirmText: string;
  cancelText?: string;
  confirmButtonColor?: string;
  isOpen: boolean;
  fromPage?: string;
  onRequestClose: () => void;
  removeFromCsv: (id) => void;
}

const FailedUploadsModal: React.FC<FailedUploadsModalProps> = ({
  failedUploads,
  cancelText = "キャンセル",
  isOpen,
  fromPage = "user",
  onRequestClose,
  removeFromCsv,
}) => {
  const modalRef: React.RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);

  const [localFailedUploads, setLocalFailedUploads] = useState(failedUploads);

  const fromUserUpload = fromPage === "user";
  const fromContactUpload = fromPage === "contact";
  const fromDepartmentUpload = fromPage === "department";

  const title = fromUserUpload
    ? "ユーザー登録エラー"
    : fromContactUpload
    ? "電話帳登録エラー"
    : fromDepartmentUpload
    ? "部署登録エラー"
    : "登録エラー";

  useEffect(() => {
    setLocalFailedUploads(failedUploads);
  }, [failedUploads]);

  const { position, handleMouseDown } = useDrag(modalRef, isOpen);
  useClickOutside(isOpen, onRequestClose, modalRef);

  const handleDeleteUser = (index: number) => {
    setLocalFailedUploads((prev) => {
      const userToDelete = prev[index];
      removeFromCsv(userToDelete.index);
      return prev.filter((_, i) => i !== index);
    });
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-800 border border-text-field-border flex items-center justify-center backdrop-brightness-50 z-[1000]">
      <div
        ref={modalRef}
        onMouseDown={handleMouseDown}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
        className="bg-white border border-text-field-border rounded-lg max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[60vw] xl:max-w-[60vw] max-h-[90vh] overflow-y-auto"
      >
        <div className="mayo-card-header w-full p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onRequestClose} className="text-xl hover:opacity-50">
            <svg
              className="ml-2 fill-body hover:fill-primary"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <XCircleIcon />
            </svg>
          </button>
        </div>

        {fromUserUpload || fromContactUpload ? (
          <FailedUsersTable
            failedUsers={localFailedUploads || []}
            handleDeleteUser={handleDeleteUser}
            externalContact={fromContactUpload}
          />
        ) : null}

        {fromDepartmentUpload ? (
          <FailedDepartmentsTable
            failedDepartments={localFailedUploads || []}
            handleDeleteDepartment={handleDeleteUser}
          />
        ) : null}

        <div className="flex justify-end gap-2.5 mb-5 mr-4">
          <button
            onClick={onRequestClose}
            className="button-small-white border-text-field-border"
          >
            {cancelText === "" ? "キャンセル" : cancelText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FailedUploadsModal;
